/**
 * Validações para cartão de crédito e CPF/CNPJ (modal Atualizar cartão).
 */

/** Algoritmo de Luhn para validar número do cartão. */
export function luhnCheck(value) {
  const digits = String(value).replace(/\D/g, '');
  if (!/^\d+$/.test(digits) || digits.length < 13) return false;
  let sum = 0;
  let alternate = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    let n = parseInt(digits[i], 10);
    if (alternate) {
      n *= 2;
      if (n > 9) n -= 9;
    }
    sum += n;
    alternate = !alternate;
  }
  return sum % 10 === 0;
}

/** Bandeiras com prefixos e tamanhos aceitos. */
const CARD_BRANDS = [
  { brand: 'amex', prefixes: ['34', '37'], lengths: [15] },
  { brand: 'visa', prefixes: ['4'], lengths: [13, 16, 19] },
  { brand: 'elo', prefixes: ['636368', '438935', '504175', '451416', '636297', '5067', '5090', '627780', '636369', '6500', '6501', '6502', '6503', '6504', '6505', '6506', '6507', '6508', '6509', '6510', '6511', '6512', '6513', '6514', '6515', '6516', '6517', '6518', '6519', '6520'], lengths: [16] },
  { brand: 'mastercard', lengths: [16], customMatch: (digits) => (digits.startsWith('5') && ['1', '2', '3', '4', '5'].includes(digits[1])) || (digits.length >= 4 && (() => { const n = parseInt(digits.slice(0, 4), 10); return n >= 2221 && n <= 2720; })()) },
];

/** Retorna a bandeira do cartão ou null se não reconhecido. */
export function getCardBrand(value) {
  const digits = String(value).replace(/\D/g, '');
  if (digits.length < 13) return null;
  for (const { brand, prefixes = [], lengths, customMatch } of CARD_BRANDS) {
    const match = customMatch ? customMatch(digits) : prefixes.some((p) => digits.startsWith(p));
    if (match && lengths.includes(digits.length)) return brand;
  }
  return null;
}

/** Tamanhos aceitos para o número (para qualquer bandeira conhecida ou genérico 13-19). */
export function getAcceptedLengths(value) {
  const brand = getCardBrand(value);
  if (brand) {
    const found = CARD_BRANDS.find((c) => c.brand === brand);
    if (found) return found.lengths;
  }
  return [13, 14, 15, 16, 17, 18, 19];
}

/** CVV: Amex 4 dígitos, demais 3. */
export function getCvvLength(value) {
  const brand = getCardBrand(value);
  return brand === 'amex' ? 4 : 3;
}

/**
 * Formata o número do cartão para exibição conforme a bandeira.
 * Amex (34/37): 4-6-5 (ex: 3782 822463 10005)
 * Demais: grupos de 4 (ex: 4544 4544 4544 4544)
 */
export function formatCardNumberDisplay(value) {
  const digits = String(value || '').replace(/\D/g, '');
  if (!digits) return '';
  const isAmex = digits.startsWith('34') || digits.startsWith('37');
  if (isAmex) {
    const max = digits.slice(0, 15);
    const p1 = max.slice(0, 4);
    const p2 = max.slice(4, 10);
    const p3 = max.slice(10, 15);
    return [p1, p2, p3].filter(Boolean).join(' ');
  }
  return digits.replace(/(\d{4})(?=\d)/g, '$1 ');
}

/** Máximo de caracteres na exibição (dígitos + espaços): Amex 17, outros 23. */
export function getCardDisplayMaxLength(value) {
  const digits = String(value || '').replace(/\D/g, '');
  const isAmex = digits.startsWith('34') || digits.startsWith('37');
  return isAmex ? 17 : 23;
}

/** Valida se mês/ano formam uma data de validade futura (ou mês atual). */
export function isExpiryFuture(month, year) {
  const m = parseInt(String(month).replace(/\D/g, ''), 10);
  let y = parseInt(String(year).replace(/\D/g, ''), 10);
  if (Number.isNaN(m) || Number.isNaN(y)) return false;
  if (y < 100) y += 2000;
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  if (y < currentYear) return false;
  if (y === currentYear && m < currentMonth) return false;
  if (m < 1 || m > 12) return false;
  return true;
}

/** Validação de CPF (11 dígitos + dígitos verificadores). */
export function isValidCPF(value) {
  const digits = String(value).replace(/\D/g, '');
  if (digits.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(digits)) return false;
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += parseInt(digits[i], 10) * (10 - i);
  let rest = (sum * 10) % 11;
  if (rest === 10) rest = 0;
  if (rest !== parseInt(digits[9], 10)) return false;
  sum = 0;
  for (let i = 0; i < 10; i++) sum += parseInt(digits[i], 10) * (11 - i);
  rest = (sum * 10) % 11;
  if (rest === 10) rest = 0;
  return rest === parseInt(digits[10], 10);
}

/** Validação de CNPJ (14 dígitos + dígitos verificadores). */
export function isValidCNPJ(value) {
  const digits = String(value).replace(/\D/g, '');
  if (digits.length !== 14) return false;
  if (/^(\d)\1{13}$/.test(digits)) return false;
  const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  let sum = 0;
  for (let i = 0; i < 12; i++) sum += parseInt(digits[i], 10) * weights1[i];
  let rest = sum % 11;
  const digit1 = rest < 2 ? 0 : 11 - rest;
  if (digit1 !== parseInt(digits[12], 10)) return false;
  const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  sum = 0;
  for (let i = 0; i < 13; i++) sum += parseInt(digits[i], 10) * weights2[i];
  rest = sum % 11;
  const digit2 = rest < 2 ? 0 : 11 - rest;
  return digit2 === parseInt(digits[13], 10);
}

/** Aceita CPF (11 dígitos) ou CNPJ (14 dígitos) válido. */
export function isValidCpfOrCnpj(value) {
  const digits = String(value).replace(/\D/g, '');
  if (digits.length === 11) return isValidCPF(value);
  if (digits.length === 14) return isValidCNPJ(value);
  return false;
}

/** Label da bandeira para exibição. */
export function getCardBrandLabel(brand) {
  const labels = { visa: 'Visa', mastercard: 'Mastercard', amex: 'American Express', elo: 'Elo' };
  return labels[brand] || brand;
}
